import { z } from "zod";
import {
  BUDGET_CATEGORY, CHECKLIST_TIMEFRAME, GUEST_CATEGORY, GUEST_RSVP, GUEST_SIDE,
  INSPIRATION_CATEGORY, SHOPPING_CATEGORY, TABLE_SHAPE, TIMELINE_VISIBILITY, VENDOR_STATUS, VENDOR_TYPE,
} from "../enums";
import { hexColor, isoDate, isoDateNullable, money, partialOf, trimmed, uuid } from "./common";

/* ---------- Guests ---------- */
export const guestSchema = z.object({
  firstName: trimmed(80).default(""),
  lastName: trimmed(80).default(""),
  email: z.union([z.literal(""), z.email()]).default(""),
  phone: trimmed(40).default(""),
  rsvp: z.enum(GUEST_RSVP).default("pending"),
  side: z.enum(GUEST_SIDE).default("both"),
  category: z.enum(GUEST_CATEGORY).default("friend"),
  partyCount: z.number().int().min(1).max(20).default(1),
  plusOneAllowed: z.boolean().default(false),
  plusOneName: trimmed(80).default(""),
  dietaryNotes: trimmed(500).default(""),
  seatingTableId: uuid.nullable().default(null),
  address: trimmed(240).default(""),
  notes: trimmed(2000).default(""),
  hasReceivedInvite: z.boolean().default(false),
  hasSentThankYou: z.boolean().default(false),
  giftDescription: trimmed(240).default(""),
  mealChoice: trimmed(80).default(""),
  householdId: uuid.nullable().default(null),
  householdName: trimmed(80).default(""),
  /** The guest's own answer about their plus-one from the RSVP page (null = not answered). Web-only. */
  plusOneAttending: z.boolean().nullable().default(null),
  /** The guest's message from the RSVP page. Web-only. */
  rsvpNote: trimmed(500).default(""),
});
export const createGuestSchema = guestSchema;
export const updateGuestSchema = partialOf(guestSchema);
export type GuestInput = z.infer<typeof guestSchema>;
export interface GuestDTO extends GuestInput {
  id: string; weddingId: string; createdAt: string; updatedAt: string;
  /** When the guest last replied through their RSVP link (server-written; null = set by the couple). */
  rsvpRespondedAt: string | null;
  /** The guest opted out of Bridewell guest email (server-written by the opt-out link). */
  mailOptOutAt: string | null;
  /** The guest's photo (server-written via the photo upload; null = none). Served by the files API. */
  photoUrl: string | null;
}

/* ---------- Budget ---------- */
export const budgetItemSchema = z.object({
  title: trimmed(120).default(""),
  category: z.enum(BUDGET_CATEGORY).default("other"),
  estimatedAmount: money.default(0),
  paidAmount: money.default(0),
  dueDate: isoDateNullable.default(null),
  vendor: trimmed(120).default(""),
  linkedVendorId: uuid.nullable().default(null),
  notes: trimmed(2000).default(""),
  isPaid: z.boolean().default(false),
});
export const createBudgetItemSchema = budgetItemSchema;
export const updateBudgetItemSchema = partialOf(budgetItemSchema);
export type BudgetItemInput = z.infer<typeof budgetItemSchema>;
export interface BudgetItemDTO extends Omit<BudgetItemInput, "dueDate" | "estimatedAmount" | "paidAmount"> {
  id: string; weddingId: string; createdAt: string; updatedAt: string;
  dueDate: string | null;
  estimatedAmount: number | null;
  paidAmount: number | null;
}

/* ---------- Checklist ---------- */
export const checklistItemSchema = z.object({
  title: trimmed(200).default(""),
  details: trimmed(2000).default(""),
  timeframe: z.enum(CHECKLIST_TIMEFRAME).default("custom"),
  isComplete: z.boolean().default(false),
  dueDate: isoDateNullable.default(null),
  sortOrder: z.number().int().default(0),
});
export const createChecklistItemSchema = checklistItemSchema;
export const updateChecklistItemSchema = partialOf(checklistItemSchema);
export type ChecklistItemInput = z.infer<typeof checklistItemSchema>;
export interface ChecklistItemDTO extends Omit<ChecklistItemInput, "dueDate"> {
  id: string; weddingId: string; createdAt: string; updatedAt: string; dueDate: string | null;
}

/* ---------- Shopping ---------- */
export const shoppingItemSchema = z.object({
  name: trimmed(120).default(""),
  quantity: z.number().int().min(1).max(9999).default(1),
  priceEach: money.default(0),
  category: z.enum(SHOPPING_CATEGORY).default("other"),
  isPurchased: z.boolean().default(false),
  link: z.union([z.literal(""), z.url()]).default(""),
  notes: trimmed(2000).default(""),
});
export const createShoppingItemSchema = shoppingItemSchema;
export const updateShoppingItemSchema = partialOf(shoppingItemSchema);
export type ShoppingItemInput = z.infer<typeof shoppingItemSchema>;
export interface ShoppingItemDTO extends Omit<ShoppingItemInput, "priceEach"> {
  id: string; weddingId: string; createdAt: string; updatedAt: string; priceEach: number | null;
}

/* ---------- Notes ---------- */
export const noteSchema = z.object({
  title: trimmed(120).default(""),
  content: trimmed(20_000).default(""),
  colorHex: hexColor.default("#F8B6C5"),
  isPinned: z.boolean().default(false),
});
export const createNoteSchema = noteSchema;
export const updateNoteSchema = partialOf(noteSchema);
export type NoteInput = z.infer<typeof noteSchema>;
export interface NoteDTO extends NoteInput { id: string; weddingId: string; createdAt: string; updatedAt: string }

/* ---------- Inspiration ---------- */
export const inspirationPhotoMetaSchema = z.object({
  caption: trimmed(240).default(""),
  category: z.enum(INSPIRATION_CATEGORY).default("other"),
  isFavorite: z.boolean().default(false),
});
export const updateInspirationPhotoSchema = partialOf(inspirationPhotoMetaSchema);
export type InspirationPhotoInput = z.infer<typeof inspirationPhotoMetaSchema>;
export interface InspirationPhotoDTO extends InspirationPhotoInput {
  id: string; weddingId: string; createdAt: string; updatedAt: string;
  /** Relative API path that streams the image after an access check. */
  imageUrl: string;
  width: number | null;
  height: number | null;
  bytes: number;
}

/* ---------- Vendors ---------- */
export const vendorSchema = z.object({
  name: trimmed(120).default(""),
  type: z.enum(VENDOR_TYPE).default("other"),
  status: z.enum(VENDOR_STATUS).default("researching"),
  contactName: trimmed(120).default(""),
  phone: trimmed(40).default(""),
  email: z.union([z.literal(""), z.email()]).default(""),
  website: z.union([z.literal(""), z.url()]).default(""),
  address: trimmed(240).default(""),
  price: money.default(0),
  deposit: money.default(0),
  notes: trimmed(4000).default(""),
  rating: z.number().int().min(0).max(5).default(0),
  depositDueDate: isoDateNullable.default(null),
  balanceDueDate: isoDateNullable.default(null),
  depositPaid: z.boolean().default(false),
  balancePaid: z.boolean().default(false),
});
export const createVendorSchema = vendorSchema;
export const updateVendorSchema = partialOf(vendorSchema);
export type VendorInput = z.infer<typeof vendorSchema>;
export interface VendorDTO extends Omit<VendorInput, "price" | "deposit" | "depositDueDate" | "balanceDueDate"> {
  id: string; weddingId: string; createdAt: string; updatedAt: string;
  price: number | null; deposit: number | null;
  depositDueDate: string | null; balanceDueDate: string | null;
  contractFilename: string;
  contractUrl: string | null;
}

/* ---------- Timeline ---------- */
export const timelineEventSchema = z.object({
  title: trimmed(120).default(""),
  time: isoDate,
  location: trimmed(240).default(""),
  notes: trimmed(2000).default(""),
  iconName: trimmed(40).default("sparkles"),
  colorHex: hexColor.default("#E94B7C"),
  durationMinutes: z.number().int().min(0).max(1440).default(30),
  linkedVendorId: uuid.nullable().default(null),
  visibility: z.enum(TIMELINE_VISIBILITY).default("team"),
});
export const createTimelineEventSchema = timelineEventSchema;
export const updateTimelineEventSchema = partialOf(timelineEventSchema);
export type TimelineEventInput = z.infer<typeof timelineEventSchema>;
export interface TimelineEventDTO extends Omit<TimelineEventInput, "time"> {
  id: string; weddingId: string; createdAt: string; updatedAt: string; time: string;
}

/* ---------- Registry ---------- */
export const registryItemSchema = z.object({
  name: trimmed(120).default(""),
  storeName: trimmed(120).default(""),
  link: z.union([z.literal(""), z.url()]).default(""),
  price: money.default(0),
  isClaimed: z.boolean().default(false),
  giftedBy: trimmed(120).default(""),
  thankYouSent: z.boolean().default(false),
  notes: trimmed(2000).default(""),
});
export const createRegistryItemSchema = registryItemSchema;
export const updateRegistryItemSchema = partialOf(registryItemSchema);
export type RegistryItemInput = z.infer<typeof registryItemSchema>;
export interface RegistryItemDTO extends Omit<RegistryItemInput, "price"> {
  id: string; weddingId: string; createdAt: string; updatedAt: string; price: number | null;
}

/* ---------- Seating ---------- */
export const seatingTableSchema = z.object({
  name: trimmed(80).default(""),
  number: z.number().int().min(1).max(999).default(1),
  capacity: z.number().int().min(1).max(99).default(8),
  notes: trimmed(500).default(""),
  x: z.number().finite().default(0),
  y: z.number().finite().default(0),
  shape: z.enum(TABLE_SHAPE).default("round"),
});
export const createSeatingTableSchema = seatingTableSchema;
export const updateSeatingTableSchema = partialOf(seatingTableSchema);
export type SeatingTableInput = z.infer<typeof seatingTableSchema>;
export interface SeatingTableDTO extends SeatingTableInput { id: string; weddingId: string; createdAt: string; updatedAt: string }

/* ---------- Activity ---------- */
export interface ActivityDTO {
  id: string;
  weddingId: string;
  actorDisplayName: string;
  actorUserId: string;
  action: "create" | "edit" | "delete";
  entityType: string;
  entityId: string;
  summary: string;
  timestamp: string;
}

/* ---------- Dashboard ---------- */
export interface DashboardDTO {
  daysUntil: number;
  isPast: boolean;
  rsvpDeadline: string | null;
  daysUntilRsvpDeadline: number | null;
  guests: { invited: number; going: number; maybe: number; declined: number; pending: number; attendingCount: number } | null;
  tasks: { done: number; total: number; overdue: number } | null;
  budget: { total: number; spent: number; estimated: number } | null;
  nextTasks: Array<{ id: string; title: string; dueDate: string | null; timeframe: string }>;
  upcomingPayments: Array<{ id: string; title: string; dueDate: string; amount: number | null; source: "budget" | "vendorDeposit" | "vendorBalance" }>;
  quote: string;
  /** Guest RSVP links (null when guests are hidden): replies arrive by themselves once links are out. */
  rsvpLinks: { enabled: boolean; links: number; replied: number; repliedOnline: number } | null;
}
